<script setup>
import { ref } from 'vue'
import { ChevronRight } from '@lucide/vue'
import { Badge, Button } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import LongText from '../../../components/LongText.vue'

const props = defineProps({ task: { type: Object, required: true } })
const { t } = useI18n()
const router = useRouter()
const expanded = ref(false)

function openTaskLogs() {
  router.push({
    path: '/intent-videos/logs',
    query: {
      scenario_id: props.task.scenario_id,
      task: props.task.id,
    },
  })
}

function statusLabel() {
  switch (props.task.status) {
    case 'pending':
    case 'prompting':
    case 'prompt_ready':
    case 'rendering':
    case 'done':
    case 'failed':
      return t(`visualGameAdmin.intentVideo.queueStatus.${props.task.status}`)
    default:
      throw new Error(`Unhandled intent video task status: ${props.task.status}, task: ${props.task.id}`)
  }
}

function updateExpanded(event) {
  expanded.value = event.currentTarget.open
}
</script>

<template>
  <details class="intent-task" @toggle="updateExpanded">
    <summary>
      <span class="intent-task__summary-main">
        <ChevronRight class="intent-task__chevron" :size="16" />
        <span class="intent-task__title">{{ t('visualGameAdmin.llm.conversations.detail.intentTask.title') }}</span>
        <Badge size="sm">{{ statusLabel() }}</Badge>
        <span class="intent-task__occurrences">{{ t('visualGameAdmin.llm.conversations.detail.intentTask.occurrences', { n: task.occurrences }) }}</span>
      </span>
      <span class="intent-task__toggle">
        {{ expanded
          ? t('visualGameAdmin.llm.calls.longText.collapse')
          : t('visualGameAdmin.llm.calls.longText.expand') }}
      </span>
    </summary>
    <div class="intent-task__body">
      <LongText
        :label="t('visualGameAdmin.llm.conversations.detail.intentTask.intent')"
        :text="task.intent_text"
        :copy-text="task.intent_text"
        :collapse-over="300"
        markdown
      />
      <Button size="sm" variant="secondary" @click="openTaskLogs">
        {{ t('visualGameAdmin.llm.conversations.detail.intentTask.openLogs', { n: task.log_count }) }}
      </Button>
    </div>
  </details>
</template>

<style scoped>
.intent-task {
  padding-top: var(--drama-space-sm);
  border-top: 1px solid var(--drama-color-border);
  font-size: var(--drama-font-size-xs);
}

.intent-task > summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
  cursor: pointer;
  list-style: none;
}

.intent-task > summary::-webkit-details-marker {
  display: none;
}

.intent-task__summary-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
}

.intent-task__chevron {
  flex: none;
  transition: transform 160ms ease;
}

.intent-task[open] .intent-task__chevron {
  transform: rotate(90deg);
}

.intent-task__title {
  color: var(--drama-color-text);
  font-weight: var(--drama-font-weight-medium);
}

.intent-task__occurrences {
  color: var(--drama-color-text-muted);
}

.intent-task__toggle {
  flex: none;
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}

.intent-task__body {
  display: grid;
  gap: var(--drama-space-md);
  width: min(72%, 680px);
  margin-top: var(--drama-space-md);
}

.intent-task__body > button {
  justify-self: start;
}

@media (max-width: 680px) {
  .intent-task__body {
    width: 100%;
  }
}
</style>
